import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType } from '@ngx-formly/material';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { assign, defaults, forEach, get } from 'lodash';
import { FormArray, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

interface TableRow {
  id: string;
  label: string;
  fields: FormlyFieldConfig[];
  tooltip?: string;
}

interface InputProps extends FormlyFieldProps {
  tableRows: TableRow[];
  addonLeft: string;
  addonRight: string;
  clearInputOnFocus: boolean;
  fullWidthLabel: boolean;
}

@Component({
    selector: 'lockton-apps-calc-table-type',
    templateUrl: './simple-table-type.component.html',
    styleUrls: ['./simple-table-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SimpleTableTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit
{
  readonly internalForm: FormArray = new FormArray<any>([]);
  readonly destroyer$: Subject<void> = new Subject();

  private get formattedInternalFormValue(): unknown {
    return assign({}, ...this.internalForm.value);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.listenToExternalChanges();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  onModelChange(): void {
    this.formControl.setValue(this.formattedInternalFormValue);
    this.formControl.markAsDirty();
  }

  private initializeForm(): void {
    this.formControl.setValidators(() =>
      this.internalForm.invalid ? { invalidAncestors: true } : null
    );

    forEach(this.props.tableRows, (row: TableRow) => {
      this.internalForm.push(new FormGroup({}));

      forEach(row.fields, (field: FormlyFieldConfig) => {
        defaults(field.props, {
          clearOnFocus: this.props.clearInputOnFocus,
          readonly: this.props.readonly,
        });

        field.defaultValue = get(
          this.field.defaultValue,
          field.key as string,
          0
        );
      });
    });
  }

  private listenToExternalChanges(): void {
    this.options.formState.validityCheck$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.internalForm.markAllAsTouched();
      });
  }
}
